/**
 * Dynamic race commentary generator.
 *
 * Given the per-horse timing curve + roster, emits a scheduled list of
 * callouts with `{ atMs, line }`. Phases:
 *   - START       (0..5%)         "AND THEY'RE OFF!" — kick off banter
 *   - EARLY       (5..30%)        out-of-the-gate flavour
 *   - MID         (30..60%)       middle stretch
 *   - LATE        (60..90%)       backstretch, late surge / kicks
 *   - PHOTO       (90..100%)      finish-line + winner callout
 *
 * Templates support placeholders:
 *   {race_number}  — 1-based race index ("RACE #3")
 *   {horse_name}   — leading horse's name at that timestamp
 *   {lane_number}  — leading horse's lane (1..8)
 *   {trailing_name}— trailing horse name at that timestamp
 *   {underdog_name}— a non-favourite that's currently doing well
 *
 * The output is deterministic given the same `(seed, timings, roster)`. We
 * use a tiny mulberry32 PRNG seeded from the race id + tick index so two
 * browser sources watching the same race see identical commentary.
 */

import type { TrackHorse } from '../state/types.js';

export interface CommentaryEvent {
  /** Ms relative to the race start (matches `progressFor` clock). */
  atMs: number;
  /** Rendered, placeholder-substituted line. */
  line: string;
}

/** Templates per phase. Placeholders inside `{...}` are replaced at render time. */
const TEMPLATES = {
  start: [
    "AND THEY'RE OFF!",
    "{race_number} IS UNDER WAY!",
    "OUT OF THE GATES!",
    "THEY'RE AWAY CLEAN!",
    "GET READY — {race_number} HAS BEGUN!",
    "ALL EIGHT BURST FROM THE GATE!",
    "THE FLAG DROPS — {race_number}!",
    "AND THEY'RE GONE — {horse_name} TAKES POSITION!",
  ],
  early: [
    "{horse_name} TAKES THE LEAD!",
    "LANE {lane_number} OUT FRONT EARLY!",
    "{horse_name} SETTING THE PACE!",
    "{horse_name} BREAKS CLEAN!",
    "{horse_name} STRIDES AHEAD!",
    "{trailing_name} STUMBLES OUT THE GATE!",
    "{horse_name} GRABS THE RAIL!",
    "{horse_name} PUSHES TO THE FRONT!",
    "EARLY LEAD TO {horse_name}!",
    "LANE {lane_number} STRETCHES OUT!",
  ],
  mid: [
    "{horse_name} HOLDS THE LEAD AT THE HALF!",
    "{underdog_name} MAKING A MOVE!",
    "HALFWAY MARK — {horse_name} OUT FRONT!",
    "{horse_name} STILL IN COMMAND!",
    "{underdog_name} CLOSES THE GAP!",
    "{horse_name} OPENS DAYLIGHT!",
    "{trailing_name} SLIPS BACK TO THE PACK!",
    "{horse_name} POWERING DOWN THE BACKSTRETCH!",
    "AT THE HALF — IT'S {horse_name} BY A LENGTH!",
    "{underdog_name} SURGES THROUGH THE PACK!",
  ],
  late: [
    "DOWN THE STRETCH — {horse_name} LEADS!",
    "{horse_name} KICKING FOR HOME!",
    "{underdog_name} CHALLENGES FOR THE LEAD!",
    "INTO THE FINAL TURN — {horse_name}!",
    "{horse_name} GOING FOR IT!",
    "{horse_name} STRAINING FOR THE WIRE!",
    "{trailing_name} IS FADING IN THE STRETCH!",
    "{underdog_name} FINDS ANOTHER GEAR!",
    "{horse_name} REFUSES TO YIELD!",
    "ONE LENGTH OF DAYLIGHT FOR {horse_name}!",
  ],
  photo: [
    "PHOTO FINISH!",
    "{horse_name} TAKES IT BY A NOSE!",
    "{horse_name} WINS {race_number}!",
    "AT THE WIRE — {horse_name}!",
    "{horse_name} HOLDS ON FOR THE WIN!",
    "WHAT A FINISH — {horse_name} BY A WHISKER!",
    "{horse_name} CROSSES FIRST!",
    "{horse_name} CLAIMS THE TROPHY!",
  ],
} as const;

type Phase = keyof typeof TEMPLATES;

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function next(): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  h ^= h >>> 13;
  h = Math.imul(h, 0x5bd1e995);
  h ^= h >>> 15;
  return h >>> 0;
}

/** Linear interpolation between segments of one horse's timing curve. */
function progressFor(h: TrackHorse, elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  if (elapsedMs >= h.timeAtFinishMs) return 1;
  const seg = (t: number, t0: number, t1: number, p0: number, p1: number) =>
    p0 + (p1 - p0) * ((t - t0) / Math.max(1, t1 - t0));
  if (elapsedMs <= h.timeAt25Ms) return seg(elapsedMs, 0, h.timeAt25Ms, 0, 0.25);
  if (elapsedMs <= h.timeAt50Ms) return seg(elapsedMs, h.timeAt25Ms, h.timeAt50Ms, 0.25, 0.5);
  if (elapsedMs <= h.timeAt75Ms) return seg(elapsedMs, h.timeAt50Ms, h.timeAt75Ms, 0.5, 0.75);
  return seg(elapsedMs, h.timeAt75Ms, h.timeAtFinishMs, 0.75, 1);
}

interface BuildOptions {
  raceId: string;
  raceIndex: number; // 0-based
  /** Total races, or 0 for continuous play. */
  racesTotal: number;
  horses: TrackHorse[];
  /** Total ms the race lasts (from broadcast). */
  raceDurationMs: number;
}

export function buildCommentary(opts: BuildOptions): CommentaryEvent[] {
  const rng = mulberry32(hashString(opts.raceId));
  const events: CommentaryEvent[] = [];

  const phases: Array<{ name: Phase; atFraction: number }> = [
    { name: 'start', atFraction: 0.02 },
    { name: 'early', atFraction: 0.18 },
    { name: 'mid', atFraction: 0.45 },
    { name: 'late', atFraction: 0.72 },
    { name: 'photo', atFraction: 0.96 },
  ];

  // Pre-sort horses by their final place for "underdog" (high lane, late
  // surge) and "trailing" (currently behind) picks.
  const finishOrder = [...opts.horses].sort((a, b) => a.finishPlace - b.finishPlace);

  for (const phase of phases) {
    const atMs = Math.round(opts.raceDurationMs * phase.atFraction);
    const tmpls = TEMPLATES[phase.name];
    const tmpl = tmpls[Math.floor(rng() * tmpls.length)] ?? tmpls[0];
    if (!tmpl) continue;

    // Find the leading horse at this moment.
    let leader = opts.horses[0];
    let leaderProgress = 0;
    for (const h of opts.horses) {
      const p = progressFor(h, atMs);
      if (p > leaderProgress) {
        leaderProgress = p;
        leader = h;
      }
    }

    // Trailing = horse with smallest progress at this moment.
    let trailing = opts.horses[0];
    let trailingProgress = Infinity;
    for (const h of opts.horses) {
      const p = progressFor(h, atMs);
      if (p < trailingProgress) {
        trailingProgress = p;
        trailing = h;
      }
    }

    // "Underdog" = a horse that finishes 2nd or 3rd but isn't currently leading.
    const underdog = finishOrder.find((h) => h.finishPlace > 1 && h.finishPlace <= 3 && h !== leader)
      ?? finishOrder[1] ?? leader;

    // For PHOTO phase, the leader is the actual winner.
    const winner = phase.name === 'photo' ? finishOrder[0] : leader;

    const line = renderTemplate(tmpl, {
      race_number: formatRaceNumber(opts.raceIndex, opts.racesTotal),
      horse_name: (winner ?? leader)?.name ?? 'THE LEADER',
      lane_number: String((winner ?? leader)?.number ?? 0),
      trailing_name: trailing?.name ?? 'THE TRAILER',
      underdog_name: underdog?.name ?? 'THE UNDERDOG',
    });
    events.push({ atMs, line });
  }

  return events;
}

function formatRaceNumber(raceIndex: number, racesTotal: number): string {
  if (racesTotal <= 0) return `RACE #${raceIndex + 1}`;
  return `RACE ${raceIndex + 1} OF ${racesTotal}`;
}

function renderTemplate(tmpl: string, vars: Record<string, string>): string {
  return tmpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

export const __TEMPLATE_COUNT = {
  start: TEMPLATES.start.length,
  early: TEMPLATES.early.length,
  mid: TEMPLATES.mid.length,
  late: TEMPLATES.late.length,
  photo: TEMPLATES.photo.length,
} as const;
